import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  SparklesIcon,
  FolderIcon,
  LifebuoyIcon,
  Cog6ToothIcon,
  UsersIcon,
  BoltIcon,
  PencilSquareIcon,
  ClockIcon,
  ArrowRightIcon,
  PlusIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { CommandPalette, CommandPaletteItem } from '../../components/Home/CommandPalette';
import { DocumentTreeMini } from '../../components/Home/DocumentTreeMini';
import { getMySignatureInbox, SignerInboxItem } from '../../services/signerInboxService';
import { useHelpdeskHomePath } from '../../hooks/useHelpdeskHomePath';

interface QuickAccessItem {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  visible: boolean;
}

const resolveGreeting = (): string => {
  const hour = new Date().getHours();

  if (hour < 12)
  {
    return 'Bom dia';
  }

  if (hour < 18)
  {
    return 'Boa tarde';
  }

  return 'Boa noite';
};

const resolveFirstName = (fullName?: string | null): string => {
  if (!fullName)
  {
    return '';
  }

  const parts = fullName.trim().split(' ').filter((part) => part.length > 0);
  if (parts.length === 0)
  {
    return '';
  }

  return parts[0];
};

const isPendingSignature = (item: SignerInboxItem): boolean => {
  return item.signerStatus !== 'Signed' && item.signerStatus !== 'Rejected';
};

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { homePath: helpdeskHomePath } = useHelpdeskHomePath();

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [pendingSignatures, setPendingSignatures] = useState<SignerInboxItem[]>([]);
  const [loadingSignatures, setLoadingSignatures] = useState(true);

  const roleCode = (user?.role?.code ?? '').toLowerCase();
  const isAdmin = Boolean(user?.isAdmin) || roleCode === 'admin';
  const isManager = roleCode === 'manager';
  const isEditor = roleCode === 'editor';
  const canManageUsers = isAdmin || isManager;
  const canAccessSupport = isAdmin || isManager;
  const canAccessWorkflows = isAdmin || isManager;
  const canAccessForms = isAdmin || isManager || isEditor;

  useEffect(() => {
    let active = true;

    const loadSignatures = async () => {
      try {
        const items = await getMySignatureInbox();
        if (active)
        {
          setPendingSignatures(items.filter(isPendingSignature));
        }
      } catch {
        if (active)
        {
          setPendingSignatures([]);
        }
      } finally {
        if (active)
        {
          setLoadingSignatures(false);
        }
      }
    };

    void loadSignatures();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k')
      {
        event.preventDefault();
        setPaletteOpen(true);
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const pendingCount = pendingSignatures.length;

  const quickAccess = useMemo<QuickAccessItem[]>(() => {
    const items: QuickAccessItem[] = [
      {
        id: 'copilot',
        label: 'Copiloto',
        description: 'Converse com a IA sobre seus documentos',
        icon: <SparklesIcon className="h-6 w-6" />,
        path: '/copilot',
        visible: true,
      },
      {
        id: 'documents',
        label: 'Documentos',
        description: 'Árvore e Drive de documentos',
        icon: <FolderIcon className="h-6 w-6" />,
        path: '/documents',
        visible: true,
      },
      {
        id: 'forms',
        label: 'Formulários',
        description: 'Criar e gerenciar formulários',
        icon: <ClipboardDocumentListIcon className="h-6 w-6" />,
        path: '/forms',
        visible: canAccessForms,
      },
      {
        id: 'signatures',
        label: 'Pendências de assinatura',
        description: pendingCount > 0 ? `${pendingCount} aguardando você` : 'Nada pendente agora',
        icon: <PencilSquareIcon className="h-6 w-6" />,
        path: '/sign/inbox',
        visible: true,
      },
      {
        id: 'workflows',
        label: 'Workflows',
        description: 'Fluxos e aprovações',
        icon: <BoltIcon className="h-6 w-6" />,
        path: '/workflows',
        visible: canAccessWorkflows,
      },
      {
        id: 'support',
        label: 'Atendimento',
        description: 'Helpdesk e conversas',
        icon: <LifebuoyIcon className="h-6 w-6" />,
        path: helpdeskHomePath,
        visible: canAccessSupport,
      },
      {
        id: 'users',
        label: 'Usuários',
        description: 'Gerenciar usuários e equipes',
        icon: <UsersIcon className="h-6 w-6" />,
        path: '/users',
        visible: canManageUsers,
      },
      {
        id: 'admin',
        label: 'Administração',
        description: 'Painel administrativo',
        icon: <Cog6ToothIcon className="h-6 w-6" />,
        path: '/admin/control-panel',
        visible: isAdmin,
      },
    ];

    return items.filter((item) => item.visible);
  }, [pendingCount, canAccessWorkflows, canAccessSupport, canManageUsers, canAccessForms, isAdmin, helpdeskHomePath]);

  const commandItems = useMemo<CommandPaletteItem[]>(() => {
    const destinations: CommandPaletteItem[] = quickAccess.map((item) => ({
      id: `goto-${item.id}`,
      label: item.label,
      hint: 'Ir para',
      group: 'Ir para',
      keywords: item.description,
      icon: item.icon,
      onSelect: () => navigate(item.path),
    }));

    const actions: CommandPaletteItem[] = [
      {
        id: 'action-new-document',
        label: 'Novo documento',
        group: 'Ações',
        keywords: 'criar upload arquivo',
        icon: <PlusIcon className="h-5 w-5" />,
        onSelect: () => navigate('/documents'),
      },
      {
        id: 'action-search',
        label: 'Buscar documentos',
        group: 'Ações',
        keywords: 'pesquisar procurar',
        icon: <MagnifyingGlassIcon className="h-5 w-5" />,
        onSelect: () => navigate('/search'),
      },
      {
        id: 'action-copilot',
        label: 'Perguntar ao Copiloto',
        group: 'Ações',
        keywords: 'ia chat inteligencia',
        icon: <SparklesIcon className="h-5 w-5" />,
        onSelect: () => navigate('/copilot'),
      },
    ];

    return [...destinations, ...actions];
  }, [quickAccess, navigate]);

  const greeting = resolveGreeting();
  const firstName = resolveFirstName(user?.name);

  return (
    <div className="max-w-5xl mx-auto w-full">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          {greeting}
          {firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="text-gray-500 mt-1">Comece pelo que importa. Busque ou execute um comando para navegar.</p>
      </div>

      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        className="w-full max-w-2xl flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 text-left text-gray-400 hover:border-gray-300 hover:shadow-sm transition-all"
      >
        <MagnifyingGlassIcon className="h-5 w-5" />
        <span className="flex-1">Buscar documentos, pessoas ou executar um comando…</span>
        <kbd className="text-[11px] bg-gray-50 border border-gray-200 rounded px-2 py-1 font-sans">Ctrl K</kbd>
      </button>

      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-3">Precisa de você agora</h2>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          {loadingSignatures ? (
            <div className="px-4 py-6 text-sm text-gray-400">Verificando pendências…</div>
          ) : pendingCount === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-500">Nada aguardando a sua ação no momento.</div>
          ) : (
            pendingSignatures.slice(0, 4).map((item) => (
              <button
                key={item.documentId}
                type="button"
                onClick={() => navigate(`/sign/inbox/${item.documentId}`)}
                className="w-full flex items-center gap-4 px-4 py-3.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 text-left"
              >
                <span className="h-9 w-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
                  <ClockIcon className="h-5 w-5" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-gray-800 truncate">{item.documentTitle}</span>
                  <span className="block text-xs text-gray-500">Assinatura pendente</span>
                </span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
                  Assinar
                </span>
              </button>
            ))
          )}
          {pendingCount > 4 && (
            <button
              type="button"
              onClick={() => navigate('/sign/inbox')}
              className="w-full flex items-center justify-center gap-1 px-4 py-2.5 text-sm font-semibold text-blue-600 hover:bg-gray-50"
            >
              Ver todas as {pendingCount} pendências
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-3">Acesso rápido</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {quickAccess.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => navigate(item.path)}
              className="flex flex-col gap-3 p-4 bg-white border border-gray-200 rounded-xl text-left hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <span className="h-11 w-11 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                {item.icon}
              </span>
              <span>
                <span className="block text-sm font-semibold text-gray-800">{item.label}</span>
                <span className="block text-xs text-gray-500 mt-0.5">{item.description}</span>
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="mt-10 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs uppercase tracking-wide text-gray-400 font-semibold">Árvore de documentos</h2>
          <button
            type="button"
            onClick={() => navigate('/documents')}
            className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700"
          >
            Abrir Drive
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-3">
          <DocumentTreeMini />
        </div>
      </section>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={commandItems} />
    </div>
  );
};
